import { defineStore } from 'pinia';
import { computed, ref } from 'vue';

/**
 * Tema dell'interfaccia: preferenza a tre vie, applicata come attributo
 * `data-theme` sull'elemento `<html>`.
 *
 * Perché non c'è nessuna chiamata al server: il tema è una preferenza di
 * DISPOSITIVO (il portatile può stare in scuro e il tablet in chiaro), quindi
 * la fonte di verità è `localStorage`. Così non si interroga l'API a ogni
 * avvio e — soprattutto — lo script inline in `index.html` può applicare il
 * tema PRIMA del primo paint leggendo la stessa chiave, senza lampeggio (FOUC).
 * Questo store è la controparte "viva": pilota il selettore delle preferenze e
 * tiene aggiornato il DOM quando la preferenza è "auto" e il sistema cambia.
 */
export type PreferenzaTema = 'auto' | 'light' | 'dark';
type TemaEffettivo = 'light' | 'dark';

const CHIAVE = 'evolfin-tema';
const consultaSistema = window.matchMedia('(prefers-color-scheme: dark)');

function leggiPreferenza(): PreferenzaTema {
  const salvata = localStorage.getItem(CHIAVE);
  return salvata === 'light' || salvata === 'dark' || salvata === 'auto' ? salvata : 'auto';
}

/** "auto" si risolve sul tema di sistema; gli altri due sono espliciti. */
function risolvi(preferenza: PreferenzaTema): TemaEffettivo {
  if (preferenza === 'auto') return consultaSistema.matches ? 'dark' : 'light';
  return preferenza;
}

/** Scrive il tema risolto sul DOM: `data-theme` guida i token e i selettori
 *  PrimeVue; `color-scheme` allinea i controlli nativi (scrollbar, input). */
function applica(effettivo: TemaEffettivo): void {
  const radice = document.documentElement;
  radice.dataset.theme = effettivo;
  radice.style.colorScheme = effettivo;
}

export const useTemaStore = defineStore('tema', () => {
  const preferenza = ref<PreferenzaTema>(leggiPreferenza());
  const effettivo = computed<TemaEffettivo>(() => risolvi(preferenza.value));

  function imposta(scelta: PreferenzaTema): void {
    preferenza.value = scelta;
    localStorage.setItem(CHIAVE, scelta);
    applica(risolvi(scelta));
  }

  // In modalità "auto" il tema segue il sistema operativo in tempo reale.
  consultaSistema.addEventListener('change', () => {
    if (preferenza.value === 'auto') applica(risolvi('auto'));
  });

  // Riallinea il DOM allo store al primo uso: idempotente rispetto allo script
  // inline (stesso risultato), serve solo se lo store nasce dopo un cambio.
  applica(effettivo.value);

  return { preferenza, effettivo, imposta };
});
